import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  submissions,
  documents,
  communications,
} from '../services/api.js';
import { DocumentUpload } from '../components/DocumentUpload.jsx';
import './SubmissionDetail.css';

function getRiskBadgeClass(tierOrScore) {
  if (tierOrScore == null) return 'badge';
  if (typeof tierOrScore === 'string') {
    if (tierOrScore === 'unscored') return 'badge';
    return `badge badge--${tierOrScore}`;
  }
  if (tierOrScore <= 2) return 'badge badge--red';
  if (tierOrScore <= 5) return 'badge badge--orange';
  if (tierOrScore <= 8) return 'badge badge--yellow';
  return 'badge badge--green';
}

function getRiskLabel(tierOrScore) {
  if (tierOrScore == null) return 'Pending';
  if (typeof tierOrScore === 'string') {
    const labels = { red: 'High Risk', orange: 'Elevated', yellow: 'Moderate', green: 'Low Risk', unscored: 'Pending' };
    return labels[tierOrScore] || tierOrScore;
  }
  if (tierOrScore <= 2) return 'High Risk';
  if (tierOrScore <= 5) return 'Elevated';
  if (tierOrScore <= 8) return 'Moderate';
  return 'Low Risk';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatKey(key) {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const CURRENCY_KEYS = /revenue|premium|limit|deductible|tiv|valuation|amount|price|cost|wage|salary|income|retention/i;

function formatValue(key, value) {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  if (typeof value === 'number' && CURRENCY_KEYS.test(key)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

const TABS = ['Overview', 'Documents', 'Missing Data', 'Communications'];

export function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');

  // Documents (still loaded separately)
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);

  // Assign UW
  const [showAssign, setShowAssign] = useState(false);
  const [selectedUw, setSelectedUw] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Follow-up form
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpTo, setFollowUpTo] = useState('');
  const [followUpSubject, setFollowUpSubject] = useState('');
  const [followUpBody, setFollowUpBody] = useState('');
  const [sendingFollowUp, setSendingFollowUp] = useState(false);
  const [followUpSent, setFollowUpSent] = useState(false);

  const loadSubmission = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await submissions.get(id);
      const subData = result.data || result;
      setSubmission(subData);
    } catch (err) {
      setError(err.message || 'Failed to load submission');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSubmission();
  }, [loadSubmission]);

  // Load docs when Documents tab is active
  useEffect(() => {
    if (!id || activeTab !== 'Documents' || docs.length > 0) return;
    setDocsLoading(true);
    documents
      .getBySubmission(id)
      .then((res) => {
        const items = res.data || res;
        setDocs(Array.isArray(items) ? items : []);
      })
      .catch(() => {})
      .finally(() => setDocsLoading(false));
  }, [activeTab, id, docs.length]);

  const handleAssign = async () => {
    if (!selectedUw) return;
    setAssigning(true);
    try {
      await submissions.assign(id, { UnderwriterID: selectedUw });
      setShowAssign(false);
      setSelectedUw('');
      loadSubmission();
    } catch {
      // Error handled silently
    } finally {
      setAssigning(false);
    }
  };

  const openFollowUp = () => {
    if (!submission) return;
    const missing = Array.isArray(submission.MissingData) ? submission.MissingData : [];
    setFollowUpSubject(
      `Missing Information Required - ${submission.InsuredName || ''} ${submission.ProductName || ''} Application`.trim()
    );
    const bulletList = missing
      .map((item) => `• ${item.Field || item.FieldName || 'Unknown'}: ${item.Description || 'Details needed'}`)
      .join('\n');
    setFollowUpBody(
      `Dear Broker,\n\nWe are missing the following information for this submission:\n\n${bulletList}\n\nPlease provide the above at your earliest convenience.\n\nThank you.`
    );
    setFollowUpTo('');
    setShowFollowUp(true);
    setFollowUpSent(false);
  };

  const handleSendFollowUp = async () => {
    if (!followUpTo) return;
    setSendingFollowUp(true);
    try {
      await communications.send({
        accountId: submission.AccountID,
        submissionId: id,
        toEmails: [followUpTo],
        subject: followUpSubject,
        body: followUpBody,
        commType: 'missing_data',
      });
      setFollowUpSent(true);
      setShowFollowUp(false);
      loadSubmission();
    } catch {
      // Error handled silently
    } finally {
      setSendingFollowUp(false);
    }
  };

  if (loading) {
    return (
      <div className="sd-loading">
        <div className="spinner" />
        <p>Loading submission...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sd-error">
        <h2>Error</h2>
        <p>{error}</p>
        <button className="btn btn--primary" onClick={loadSubmission}>
          Retry
        </button>
      </div>
    );
  }

  if (!submission) return null;

  const sub = submission;
  const structuredData = sub.StructuredData || {};
  const missingData = Array.isArray(sub.MissingData) ? sub.MissingData : [];
  const comms = Array.isArray(sub.Communications) ? sub.Communications : [];
  const availableUW = Array.isArray(sub.AvailableUnderwriters) ? sub.AvailableUnderwriters : [];
  const isAssigned = !!sub.AssignedUW;

  return (
    <div className="sd">
      {/* Breadcrumb */}
      <div className="sd__breadcrumb">
        <button className="sd__back" onClick={() => navigate('/')}>
          &larr; Back to Queue
        </button>
      </div>

      {/* Header */}
      <div className="sd__header card">
        <div className="sd__header-main">
          <h1 className="sd__insured">
            {sub.InsuredName || sub.AccountID || 'Submission'}
          </h1>
          <div className="sd__header-badges">
            <span className={getRiskBadgeClass(sub.RiskTier || sub.RiskScore)}>
              {sub.RiskScore != null
                ? `${sub.RiskScore} · ${getRiskLabel(sub.RiskTier || sub.RiskScore)}`
                : 'Score Pending'}
            </span>
            <span className="sd__status-badge" data-status={sub.Status}>
              {(sub.Status || 'new').replace(/_/g, ' ')}
            </span>
          </div>
        </div>
        <div className="sd__header-meta">
          <span>
            Product: <strong>{sub.ProductName || sub.ProductID || '—'}</strong>
          </span>
          <span>
            Broker: <strong>{sub.BrokerName || sub.BrokerID || '—'}</strong>
          </span>
          <span>
            Assigned UW: <strong>{sub.AssignedUWName || sub.AssignedUW || 'Unassigned'}</strong>
          </span>
          <span>
            Source: <strong>{sub.Source || '—'}</strong>
          </span>
          <span>
            Created: <strong>{formatDate(sub.CreatedAt)}</strong>
          </span>
        </div>
        <div className="sd__header-actions">
          {!isAssigned && (
            <button
              className="btn btn--secondary"
              onClick={() => setShowAssign(!showAssign)}
            >
              Assign Underwriter
            </button>
          )}
          {missingData.length > 0 && (
            <button
              className="btn btn--secondary"
              onClick={openFollowUp}
            >
              Send Missing Data Follow-up
            </button>
          )}
          <button
            className="btn btn--primary sd__canvas-btn"
            onClick={() => navigate(`/underwriting/${id}`)}
          >
            Open Underwriting Canvas
          </button>
        </div>

        {/* Assign panel */}
        {showAssign && (
          <div className="sd__assign-panel">
            <select
              value={selectedUw}
              onChange={(e) => setSelectedUw(e.target.value)}
              className="sd__assign-select"
            >
              <option value="">Select underwriter...</option>
              {availableUW.map((u) => (
                <option
                  key={u.UnderwriterID}
                  value={u.UnderwriterID}
                >
                  {u.Name} ({u.CurrentWorkload}/{u.MaxDailyCapacity})
                </option>
              ))}
            </select>
            <button
              className="btn btn--primary"
              onClick={handleAssign}
              disabled={!selectedUw || assigning}
            >
              {assigning ? 'Assigning...' : 'Confirm'}
            </button>
            <button className="btn btn--secondary" onClick={() => setShowAssign(false)}>
              Cancel
            </button>
          </div>
        )}

        {/* Follow-up form */}
        {showFollowUp && (
          <div className="sd__followup-form">
            <h4 className="sd__followup-title">Send Missing Data Follow-up</h4>
            <div className="sd__followup-field">
              <label className="sd__followup-label">To</label>
              <input
                type="email"
                value={followUpTo}
                onChange={(e) => setFollowUpTo(e.target.value)}
                placeholder="broker@example.com"
                className="sd__followup-input"
              />
            </div>
            <div className="sd__followup-field">
              <label className="sd__followup-label">Subject</label>
              <input
                type="text"
                value={followUpSubject}
                onChange={(e) => setFollowUpSubject(e.target.value)}
                className="sd__followup-input"
              />
            </div>
            <div className="sd__followup-field">
              <label className="sd__followup-label">Body</label>
              <textarea
                value={followUpBody}
                onChange={(e) => setFollowUpBody(e.target.value)}
                className="sd__followup-textarea"
                rows={8}
              />
            </div>
            <div className="sd__followup-actions">
              <button
                className="btn btn--primary"
                onClick={handleSendFollowUp}
                disabled={!followUpTo || sendingFollowUp}
              >
                {sendingFollowUp ? 'Sending...' : 'Send'}
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => setShowFollowUp(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {followUpSent && (
          <div className="sd__followup-success">
            Follow-up email sent successfully!
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="sd__tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            className={`sd__tab ${activeTab === tab ? 'sd__tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
            {tab === 'Missing Data' && missingData.length > 0 && (
              <span className="sd__tab-count">{missingData.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="sd__content card">
        {activeTab === 'Overview' && (
          <div className="sd__overview">
            <h3 className="sd__section-title">Structured Data</h3>
            {structuredData && Object.keys(structuredData).length > 0 ? (
              <dl className="sd__data-grid">
                {Object.entries(structuredData).map(([key, value]) => (
                  <div key={key} className="sd__data-item">
                    <dt>{formatKey(key)}</dt>
                    <dd>{formatValue(key, value)}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="sd__empty">No structured data extracted yet.</p>
            )}
          </div>
        )}

        {activeTab === 'Documents' && (
          <div className="sd__documents">
            <h3 className="sd__section-title">Upload Document</h3>
            <DocumentUpload
              submissionId={id}
              accountId={sub.AccountID}
              onComplete={() => {
                setDocs([]);
                loadSubmission();
              }}
            />
            <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: 'var(--space-lg) 0' }} />
            <h3 className="sd__section-title">Submission Documents</h3>
            {docsLoading ? (
              <div className="sd__tab-loading">
                <div className="spinner" />
              </div>
            ) : Array.isArray(docs) && docs.length > 0 ? (
              <>
              <table className="sd__doc-table">
                <thead>
                  <tr>
                    <th>Document Name</th>
                    <th>Type</th>
                    <th>Uploaded</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((doc) => {
                    const status = doc.ProcessingStatus || doc.Status || 'pending';
                    const statusClass = status === 'processed' ? 'badge--green'
                      : status === 'failed' ? 'badge--red'
                      : status === 'extracting' || status === 'analyzing' ? 'badge--yellow'
                      : 'badge--orange';
                    return (
                      <tr key={doc.DocumentID || doc.SK}>
                        <td className="sd__doc-name">
                          {doc.FileName || doc.Name || '—'}
                        </td>
                        <td>{doc.DocType || doc.DocumentType || doc.FileType || '—'}</td>
                        <td>{formatDate(doc.CreatedAt)}</td>
                        <td>
                          <span className={`badge ${statusClass}`}>
                            {status}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn--secondary"
                            style={{ padding: '2px 8px', fontSize: '12px' }}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Delete ${doc.FileName || 'this document'}?`)) return;
                              try {
                                await documents.delete(doc.DocumentID);
                                setDocs((prev) => prev.filter((d) => d.DocumentID !== doc.DocumentID));
                              } catch (err) {
                                alert('Failed to delete: ' + err.message);
                              }
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Extraction Results Notice */}
              {docs.some((d) => (d.ProcessingStatus || d.Status) === 'processed') && (
                <div className="sd__extraction-notice" style={{
                  marginTop: 'var(--space-md)',
                  padding: 'var(--space-md)',
                  background: 'var(--color-risk-green-bg)',
                  borderRadius: 'var(--radius-md)',
                  borderLeft: '4px solid var(--color-risk-green)',
                }}>
                  <strong style={{ color: 'var(--color-risk-green)' }}>Extraction Complete</strong>
                  <p style={{ margin: 'var(--space-xs) 0 0', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                    Document data has been extracted and merged into this submission.
                    Check the <strong>Overview</strong> tab for extracted structured data
                    and the <strong>Missing Data</strong> tab for flagged items.
                  </p>
                </div>
              )}
              </>
            ) : (
              <p className="sd__empty">No documents uploaded yet.</p>
            )}
          </div>
        )}

        {activeTab === 'Missing Data' && (
          <div className="sd__missing">
            <h3 className="sd__section-title">Missing & Flagged Data</h3>
            {missingData.length > 0 ? (
              <ul className="sd__missing-list">
                {missingData.map((item, idx) => {
                  const isString = typeof item === 'string';
                  return (
                    <li key={isString ? item : idx} className="sd__missing-item">
                      <div className="sd__missing-field">
                        <span className="sd__missing-name">
                          {isString ? item : item.Field || item.FieldName || item.Name || `Item ${idx + 1}`}
                        </span>
                        {!isString && item.Required && (
                          <span className="sd__required-tag">Required</span>
                        )}
                        {!isString && item.Category && (
                          <span className="sd__category-tag">{item.Category}</span>
                        )}
                      </div>
                      {!isString && item.Description && (
                        <p className="sd__missing-desc">{item.Description}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="sd__empty">No missing data flagged for this submission.</p>
            )}
          </div>
        )}

        {activeTab === 'Communications' && (
          <div className="sd__comms">
            <h3 className="sd__section-title">Communication History</h3>
            {comms.length > 0 ? (
              <div className="sd__comms-timeline">
                {comms.map((msg, idx) => (
                  <div key={msg.CommunicationID || idx} className="sd__comm-item">
                    <div className="sd__comm-header">
                      <strong>{msg.Type || msg.CommType || 'Message'}</strong>
                      <span className="sd__comm-date">
                        {formatDate(msg.SentAt || msg.CreatedAt)}
                      </span>
                    </div>
                    <p className="sd__comm-subject">{msg.Subject || ''}</p>
                    <p className="sd__comm-body">{msg.Body || ''}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="sd__empty">No communications for this submission.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
